import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { SITE_URL } from "@/lib/seoMeta";
import { IMAGES } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContext";
import { loc } from "@/lib/localize";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { Button } from "@/components/ui/button";

export default function LegalPage() {
  const { slug } = useParams();
  const { L, isAr } = useLang();
  const { settings } = useSite();
  const { data: page, isLoading, isError } = useQuery({
    queryKey: ["legal", slug],
    queryFn: async () => (await api.get(`/legal/${slug}`)).data,
    retry: false,
  });
  useSeo({ title: page && loc(page, "title", isAr), description: page && loc(page, "title", isAr), canonical: `${SITE_URL}/legal/${slug}`, image: IMAGES.skyline, locale: isAr ? "ar_AE" : "en_US" });

  if (isLoading) return <div className="container-tight py-32 text-center text-subtle">...</div>;
  if (isError || !page) return (
    <div className="container-tight py-32 text-center">
      <h1 className="font-heading text-2xl font-semibold text-ink">404</h1>
      <Button asChild className="mt-6 bg-finblue hover:bg-finblueDark"><Link to="/legal">{L.pages.legal.title}</Link></Button>
    </div>
  );

  return (
    <div className="py-14">
      <div className="container-tight max-w-3xl">
        <Breadcrumbs items={[{ label: L.common.home, to: "/" }, { label: L.pages.legal.overline, to: "/legal" }, { label: loc(page, "title", isAr) }]} />
        <h1 className="font-heading text-3xl font-bold text-ink lg:text-4xl">{loc(page, "title", isAr)}</h1>
        <p className="mt-2 text-sm text-subtle">{L.pages.legal.updated}: {new Date(page.updated_at).toLocaleDateString(isAr ? "ar" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
        <div className="prose-legal mt-8" dangerouslySetInnerHTML={{ __html: loc(page, "body", isAr) }} />
        {settings.legal_email && (
          <p className="mt-8 border-t border-hair pt-6 text-sm text-subtle" data-testid="legal-contact-email">
            {isAr ? "لأي استفسارات قانونية:" : "For legal enquiries:"}{" "}
            <a href={`mailto:${settings.legal_email}`} className="font-semibold text-finblue">{settings.legal_email}</a>
          </p>
        )}
      </div>
    </div>
  );
}
